// main app page file

import './../public/index.css'
import { GlobalContext } from './context'

import React, { useEffect, useState } from 'react'
import Head from 'next/head'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { Provider } from 'react-redux'

import { enableExperimentalFragmentVariables } from 'graphql-tag'
import { loadErrorMessages, loadDevMessages } from '@apollo/client/dev'

import app from '@app/app.module.sass'
import { wrapper } from '@app/model/store'
import { PageLayoutSkeleton } from '@app/PageLayout'

import AuthEventsScript from '@shared/lib/helpers/externalScripts/authEventsScript'
import EventMarketScript from '@shared/lib/helpers/externalScripts/eventMarketScript'
import CapirsAsyncScript from '@shared/lib/helpers/externalScripts/capirsAsyncScript'
import EggClickHouse from '@shared/lib/helpers/externalScripts/eggHouseScript'
import { YaMetrics } from '@shared/lib/helpers/yaMetrics'

import { FontsProvider } from '@app/providers/FontsProvider'
import { HeaderAdvert } from '@app/PageLayout/components/HeaderAdvert'
import { addQaToUrl, getIsActiveQA } from '@shared/lib/helpers/autoTests/helpers'
import { BottomFixedContent } from '../src/3_widgets/Common/BottomFixedContent'
import createConsoleExtension from '../src/6_shared/lib/helpers/advancedConsole'

// *** constants ***
const AFISHA_NAME = process.env.AFISHA_NAME
const IS_DEV = process.env.NODE_ENV === 'development'
const GITLAB_STATUS = process.env.GITLAB_STATUS || null

const IS_PROD = GITLAB_STATUS === '0'
const IS_STAND = GITLAB_STATUS === '1'

// *** setup ***
Error.stackTraceLimit = 100

enableExperimentalFragmentVariables()

if (IS_DEV) {
  loadDevMessages()
  loadErrorMessages()
}

const ClientRouterEffects = dynamic(() => import('./client-router-effects'), { ssr: false })

interface AppContext {
  isSkeletonPage: boolean
  skeletonRoute?: string
}

interface AppProps {
  Component: React.ComponentType<any>
  pageProps: any
}

createConsoleExtension()

function MyApp({ Component, pageProps }: AppProps) {
  const { store } = wrapper.useWrappedStore(pageProps)
  // почему это стейт с типом, а не контекст?
  const [context, setContext] = useState<AppContext>({ isSkeletonPage: false })
  const router = useRouter()

  /**
   * @description START #rcmrclid=c579607b33b7efd3
   * @description RCM рекомендательная система
   * @todo takeout to custom hook
   * @todo перенести в node/express
   */

  const TARGET_RCM_KEY = 'rcmrclid'
  const [isRcmEnabled, setIsRcmEnabled] = useState(false)
  const [clientRcmValue, setClientRcmValue] = useState<string | null>(null)

  const addRcmToUrl = (rcmHashValue: string) => {
    if (typeof window !== 'undefined' && router?.isReady) {
      router.push({ hash: `${TARGET_RCM_KEY}=${rcmHashValue}` })
    }
  }

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleRcm = () => {
      if (!router?.isReady) return

      const isRCM = window?.location?.hash?.indexOf(TARGET_RCM_KEY) > 0
      if (isRCM) {
        const rcmValue = window.location.hash
          .replace('#', '')
          .split('&')
          .filter((item) => item.includes(TARGET_RCM_KEY))[0]
          .split('=')[1]
        setClientRcmValue(rcmValue)
        setIsRcmEnabled(true)
      } else {
        isRcmEnabled && clientRcmValue && addRcmToUrl(clientRcmValue)
      }
    }

    handleRcm()
  }, [router?.isReady])

  /**
   * @description END rcmrclid
   */

  return (
    <>
      <Head>
        <title>{AFISHA_NAME}</title>

        <meta charSet='utf-8' />
        <meta name='viewport' content='width=device-width, initial-scale=1, user-scalable=no' />
        <meta name='theme-color' content='#ffffff' />

        <meta
          name='description'
          content='Вся информация, чтобы спланировать хороший отдых: кухня, средний чек, рейтинги и отзывы, фото, адреса и телефоны. Гид по ресторанам, барам, пабам и кафе Москвы.'
        />

        <link rel='icon' href='/favicon.ico' />
      </Head>

      <FontsProvider>
        <ClientRouterEffects setContext={setContext} />
        <Provider store={store}>
          <>
            {/* important scripts */}
            <AuthEventsScript />
            <EventMarketScript />
            <CapirsAsyncScript />
          </>

          <GlobalContext.Provider value={[context, setContext]}>
            <HeaderAdvert />

            <div id='next-content' className={app.root}>
              {!context.isSkeletonPage ? (
                <Component {...pageProps} />
              ) : (
                <PageLayoutSkeleton route={context.skeletonRoute} />
              )}
              <BottomFixedContent />
              <div id='next-gig-assist-content'></div>
            </div>
          </GlobalContext.Provider>
        </Provider>
      </FontsProvider>

      {/* Metrics script's START */}
      <>
        <EggClickHouse />
        <YaMetrics />
      </>
      {/* Metrics script's END */}
    </>
  )
}

export default MyApp
